import PropTypes from "prop-types";
import AppContext from "../../AppContext";
import React, { useContext, useEffect, useState } from "react";
import useScreenSize from "../../hooks/useScreenSize.js";
import useBlogs from "../../hooks/useBlogs.js";
import { langComponent, PAGE_STATE } from "../../constants/index.jsx";
import useUsers from "../../hooks/useUsers.js";
import { format } from "date-fns";
import "../../styles/blog.scss";
import DOMPurify from "dompurify";
import ScrollToTop from "../common/ScrollToTop/index.jsx";
import { ReactComponent as FacebookSvg } from "../../assets/icons/facebook.svg";
import { ReactComponent as LineSvg } from "../../assets/icons/line-outline.svg";
import { ReactComponent as LinkedInSvg } from "../../assets/icons/linkedin.svg";
import { ReactComponent as EmailSvg } from "../../assets/icons/email.svg";
import { ReactComponent as ArrowLeftSvg } from "../../assets/icons/arrow-left.svg";
import { ReactComponent as EyeSvg } from "../../assets/icons/eye-gray.svg";
import { useNavigate } from "react-router-dom";
import ContentLoader from "react-content-loader";
import Chip from "../../components/common/Chip";
import { FacebookShareButton, LineShareButton } from "react-share";
import {
  BalloonEditor,
  Context,
  Code,
  Bold,
  Essentials,
  Italic,
  Paragraph,
  Heading,
  BlockQuote,
  Font,
  Link,
  List,
  Underline,
  Strikethrough,
  RemoveFormat,
  Alignment,
  Indent,
  IndentBlock,
  AutoImage,
  Image,
  Base64UploadAdapter,
  ImageInsert,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageCaption,
  PictureEditing,
  MediaEmbed,
  FullPage,
  Autoformat,
  AutoLink,
  SourceEditing,
  ContextWatchdog,
} from "ckeditor5";
import { CKEditor, CKEditorContext } from "@ckeditor/ckeditor5-react";

import "ckeditor5/ckeditor5.css";
const BlogDetail = ({ _id }) => {
  const { getBlog, addBlockViews } = useBlogs();
  const { queryDesignWorks } = useUsers();
  const [initialValues, setInitialValues] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);
  const navigate = useNavigate();
  const fetchData = async () => {
    const {
      name,
      content,
      category,
      cover,
      effectiveDate,
      desc,
      status,
      credit,
      views,
      tags,
    } = await getBlog(_id);
    await addBlockViews(_id);
    setInitialValues({
      name,
      content,
      category,
      desc,
      cover,
      effectiveDate,
      status,
      credit,
      views,
      tags,
    });
    setIsLoaded(true);
  };
  const handleBack = () => {
    navigate(-1);
  };
  const formatView = (n) => {
    if (n < 1e3) return n;
    if (n >= 1e3) return +(n / 1e3).toFixed(1) + "K";
  };
  const loadingState = (props) => {
    return (
      <ContentLoader
        speed={1}
        width={800}
        height={800}
        viewBox="0 0 1000 1000"
        {...props}
      >
        <rect x="0" y="1" rx="2" ry="2" width="500" height="50" />
        <rect x="0" y="70" rx="2" ry="2" width="150" height="30" />
        <rect x="200" y="70" rx="2" ry="2" width="300" height="30" />
        <rect x="0" y="120" rx="20" ry="20" width="150" height="40" />
        <rect x="180" y="120" rx="20" ry="20" width="150" height="40" />
        <rect x="360" y="120" rx="20" ry="20" width="150" height="40" />
        <rect x="0" y="170" rx="2" ry="2" width="500" height="40" />
        <rect x="0" y="220" rx="30" ry="30" width="1000" height="550" />
        <rect x="0" y="780" rx="2" ry="2" width="1000" height="40" />
        <rect x="0" y="830" rx="2" ry="2" width="1000" height="40" />
        <rect x="0" y="880" rx="2" ry="2" width="1000" height="40" />
      </ContentLoader>
    );
  };
  useEffect(() => {
    if (!_id) return;
    fetchData();
    return () => {};
  }, []);
  const sanitizedHTML = DOMPurify.sanitize(initialValues.content);
  return (
    <div className="blog-detail">
      <ScrollToTop />
      <div className="blog-detail-header">
        <div className="blog-detail-header-action" onClick={handleBack}>
          <ArrowLeftSvg />
          <p>Back</p>
        </div>
      </div>
      {isLoaded ? (
        <>
          <div className="blog-detail-content">
            <div className="blog-detail-content-name">
              <p>{initialValues.name}</p>
            </div>
            <div className="blog-detail-content-header-text">
              {initialValues.effectiveDate && (
                <>
                  <div className="blog-detail-content-header-text__date">
                    {format(
                      new Date(initialValues.effectiveDate),
                      "dd/MM/yyyy"
                    )}
                  </div>
                  <div>{"|"}</div>
                </>
              )}

              <div className="blog-detail-content-header-text__view">
                <EyeSvg />
                {initialValues.views ? formatView(initialValues.views) : 0}
              </div>
            </div>
            <div className="blog-detail-content-header-tags">
              {initialValues.tags
                ? initialValues.tags.map((tag, index) => {
                    return (
                      <div
                        key={`tags-key-${index}`}
                        className="blog-detail-content-header-tags__chip"
                        onClick={() => navigate(`/blogs?tag=${tag}`)}
                      >
                        {tag}
                      </div>
                    );
                  })
                : ""}
            </div>
            <div
              className="blog-detail-content-header-credit"
              style={{ display: initialValues.credit ? "flex" : "none" }}
            >
              <p className="blog-detail-content-header-credit__text">
                Credit:{" "}
              </p>
              <p className="blog-detail-content-header-credit__value">
                {initialValues.credit}
              </p>
            </div>
            <div className="blog-detail-content-body">
              <CKEditorContext
                context={Context}
                contextWatchdog={ContextWatchdog}
              >
                <CKEditor
                  editor={BalloonEditor}
                  disabled
                  config={{
                    placeholder: "Type or paste your content here!",
                    plugins: [
                      Essentials,
                      Font,
                      Heading,
                      Code,
                      Bold,
                      Italic,
                      Paragraph,
                      Underline,
                      Strikethrough,
                      BlockQuote,
                      List,
                      Indent,
                      IndentBlock,
                      Link,
                      AutoImage,
                      Image,
                      ImageInsert,
                      ImageResize,
                      ImageStyle,
                      ImageToolbar,
                      ImageCaption,
                      PictureEditing,
                      MediaEmbed,
                      FullPage,
                      Alignment,
                      RemoveFormat,
                      Autoformat,
                      AutoLink,
                      Base64UploadAdapter,
                      SourceEditing,
                    ],
                    toolbar: [],

                    htmlSupport: {
                      allow: [
                        // Enables all HTML features.
                        {
                          name: /.*/,
                          attributes: true,
                          classes: true,
                          styles: true,
                        },
                      ],
                      disallow: [
                        {
                          attributes: [
                            { key: /^on(.*)/i, value: true },
                            {
                              key: /.*/,
                              value:
                                /(\b)(on\S+)(\s*)=|javascript:|(<\s*)(\/*)script/i,
                            },
                            {
                              key: /.*/,
                              value: /data:(?!image\/(png|jpeg|gif|webp))/i,
                            },
                          ],
                        },
                        { name: "script" },
                      ],
                    },
                  }}
                  data={sanitizedHTML}
                />
              </CKEditorContext>
            </div>
          </div>
          <div className="blog-detail-footer">
            <div className="blog-detail-footer__span">
              <span>Share this</span>
            </div>
            <div className="blog-detail-footer-icon">
              {/* <div className="blog-detail-footer-icon__email">
                <EmailSvg />
              </div> */}
              <div className="blog-detail-footer-icon__line">
                <LineShareButton url={`https://urbaan.app/blogs/${_id}`}>
                  <div className="profile-share-list-item__icon--small">
                    <LineSvg className="profile-share-list-item__icon" />
                  </div>
                </LineShareButton>
              </div>
              {/* <div className="blog-detail-footer-icon__linkedin">
                <LinkedInSvg />
              </div> */}
              <div className="blog-detail-footer-icon__facebook">
                <FacebookShareButton
                  url={`https://urbaan.app/blogs/${_id}?picture=${initialValues.cover}`}
                >
                  <div className="profile-share-list-item__icon--small">
                    <FacebookSvg className="profile-share-list-item__icon--small" />
                  </div>
                </FacebookShareButton>
              </div>
            </div>
          </div>
        </>
      ) : (
        loadingState()
      )}
    </div>
  );
};
BlogDetail.propTypes = {
  _id: PropTypes.string,
};

export default BlogDetail;
